import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Trash2, Plus, Pencil, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", cover_image: "", category: "Wellness", read_time: "4 min read", content: "" });

  const load = async () => { const { data } = await api.get("/blog"); setPosts(data); };
  useEffect(() => { load(); }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openCreate = () => { setEditing(null); setForm({ slug: "", title: "", excerpt: "", cover_image: "", category: "Wellness", read_time: "4 min read", content: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/blog/${editing.slug}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", form);
        toast.success("Post created");
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${slug}`);
    toast.success("Post deleted");
    await load();
  };

  return (
    <div data-testid="admin-blog-tab" className="space-y-5">
      <div className="flex justify-end">
        <button data-testid="blog-create-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-800"><Plus className="w-4 h-4" strokeWidth={1.5}/> New post</button>
      </div>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.slug} data-testid={`blog-row-${p.slug}`} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-center gap-4">
            {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-14 object-cover rounded-xl bg-welljio-tint"/>}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold line-clamp-1">{p.title}</p>
              <p className="text-xs text-welljio-muted">{p.category} · {p.read_time}</p>
            </div>
            <button data-testid={`blog-edit-${p.slug}`} onClick={() => openEdit(p)} className="text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-welljio-tint inline-flex items-center gap-1.5"><Pencil className="w-3 h-3" strokeWidth={1.5}/> Edit</button>
            <button data-testid={`blog-delete-${p.slug}`} onClick={() => remove(p.slug)} className="text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-red-50 text-red-600"><Trash2 className="w-3 h-3" strokeWidth={1.5}/></button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <input required disabled={!!editing} value={form.slug} onChange={change("slug")} placeholder="slug" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent disabled:bg-welljio-tint"/>
            <input required value={form.title} onChange={change("title")} placeholder="Title" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <input value={form.cover_image} onChange={change("cover_image")} placeholder="Cover image URL" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.category} onChange={change("category")} placeholder="Category" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
              <input value={form.read_time} onChange={change("read_time")} placeholder="Read time" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            </div>
            <textarea value={form.excerpt} onChange={change("excerpt")} rows={2} placeholder="Excerpt" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <textarea value={form.content} onChange={change("content")} rows={6} placeholder="Content" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <button type="submit" className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800">{editing ? "Save changes" : "Create post"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", rating: 5, story: "", product: "" });

  const load = async () => { const { data } = await api.get("/testimonials"); setItems(data); };
  useEffect(() => { load(); }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/testimonials", { ...form, rating: parseInt(form.rating) || 5 });
      toast.success("Testimonial added");
      setForm({ name: "", city: "", rating: 5, story: "", product: "" });
      await load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <div data-testid="admin-testimonials-tab" className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={create} className="bg-white border border-slate-100 rounded-3xl p-6 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-semibold">Add testimonial</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required value={form.name} onChange={change("name")} placeholder="Name" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
          <input value={form.city} onChange={change("city")} placeholder="City" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        </div>
        <input value={form.product} onChange={change("product")} placeholder="Product (optional)" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        <select value={form.rating} onChange={change("rating")} className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none bg-white">
          {[5,4,3,2,1].map((r) => <option key={r} value={r}>{r} star{r>1?"s":""}</option>)}
        </select>
        <textarea required value={form.story} onChange={change("story")} rows={4} placeholder="Story" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        <button data-testid="testimonial-submit" type="submit" className="w-full bg-welljio-ink text-white rounded-full py-3 text-sm font-medium hover:bg-slate-800">Add testimonial</button>
      </form>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="bg-white border border-slate-100 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-welljio-muted">{t.city}</p>
              </div>
              <div className="flex items-center gap-1">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3 h-3 fill-welljio-ink text-welljio-ink" strokeWidth={1}/>)}</div>
            </div>
            <p className="text-sm text-welljio-muted line-clamp-3 mb-3">"{t.story}"</p>
            <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="text-xs text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "", description: "" });

  const load = async () => { const { data } = await api.get("/categories"); setCats(data); };
  useEffect(() => { load(); }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", form);
      toast.success("Category added");
      setForm({ slug: "", name: "", description: "" });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${slug}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <div data-testid="admin-categories-tab" className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={create} className="bg-white border border-slate-100 rounded-3xl p-6 space-y-3 h-fit">
        <h3 className="font-heading text-lg font-semibold">Add category</h3>
        <input required value={form.slug} onChange={change("slug")} placeholder="slug (e.g. mobility-tools)" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        <input required value={form.name} onChange={change("name")} placeholder="Name" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        <textarea value={form.description} onChange={change("description")} rows={3} placeholder="Description" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
        <button data-testid="category-submit" type="submit" className="w-full bg-welljio-ink text-white rounded-full py-3 text-sm font-medium hover:bg-slate-800">Add category</button>
      </form>
      <div className="space-y-3">
        {cats.map((c) => (
          <div key={c.slug} data-testid={`category-row-${c.slug}`} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium">{c.name}</p>
              <p className="text-xs text-welljio-muted line-clamp-2">{c.description || "—"}</p>
              <p className="text-xs text-welljio-subtle mt-1">/{c.slug}</p>
            </div>
            <button data-testid={`category-delete-${c.slug}`} onClick={() => remove(c.slug)} className="text-xs text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminContentPage() {
  return (
    <div data-testid="admin-content-page" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Storefront</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Content</h1>
      </div>
      <Tabs defaultValue="blog" className="space-y-6">
        <TabsList className="bg-welljio-tint rounded-full p-1">
          <TabsTrigger value="blog" data-testid="content-tab-blog" className="rounded-full px-6">Blog</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="content-tab-testimonials" className="rounded-full px-6">Testimonials</TabsTrigger>
          <TabsTrigger value="categories" data-testid="content-tab-categories" className="rounded-full px-6">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="blog"><BlogManager/></TabsContent>
        <TabsContent value="testimonials"><TestimonialsManager/></TabsContent>
        <TabsContent value="categories"><CategoriesManager/></TabsContent>
      </Tabs>
    </div>
  );
}
